// Customer segments specific to Phuket island
// Each segment has a distinct content angle to avoid duplicate content

export interface PhuketSegment {
  slug: string;
  emoji: string;
  titleTh: string;
  titleEn: string;
  shortTh: string;
  metaTitle: string;
  metaDesc: string;
  hero: { headline: string; sub: string };
  pains: string[];
  services: string[]; // service slugs from SERVICES
  areas: string[]; // area slugs they typically live/work in
  visas: string[];
  faq: { q: string; a: string }[];
}

export const PHUKET_SEGMENTS: PhuketSegment[] = [
  {
    slug: "expats",
    emoji: "🌴",
    titleTh: "Expat ในภูเก็ต",
    titleEn: "Expats Living in Phuket",
    shortTh: "ชาวต่างชาติที่พำนักระยะยาวบนเกาะ — Retirement, LTR, O-A, Marriage Visa",
    metaTitle: "Notary & Visa Services for Expats in Phuket",
    metaDesc: "Full legal support for expats in Phuket — Retirement Visa, LTR, marriage, property POA, document translation in 40+ languages. On-site at Patong, Laguna, Rawai.",
    hero: {
      headline: "ครบทุกบริการสำหรับ Expat ในภูเก็ต — จากต่ออายุวีซ่าถึง POA วิลล่า",
      sub: "ทีมพูดได้ English / Русский / 中文 / Deutsch / Français — บริการ on-site ที่บ้าน วิลล่า คอนโดทั่วเกาะ",
    },
    pains: [
      "ต่อวีซ่า O-A / Retirement ที่ ตม. ภูเก็ต ใช้เวลานาน เอกสารเยอะ",
      "ต้องแปลและรับรองเอกสารจากประเทศต้นทาง (Police Clearance, Health Cert, Bank Letter)",
      "ไม่อยากบินไปกรุงเทพเพื่อรับรองเอกสารที่สถานทูต",
      "ต้องการคนช่วยคุยกับหน่วยงานราชการเป็นภาษาไทย",
    ],
    services: ["notary-en", "translate-all", "embassy", "police-intl", "thai-visa", "wp"],
    areas: ["patong", "rawai", "kata", "cherngtalay", "kamala"],
    visas: ["LTR (Long-Term Resident)", "Retirement O-A / O-X", "Non-O (Marriage)", "Thailand Privilege (Elite)"],
    faq: [
      { q: "ต่อ Retirement Visa ที่ ตม. ภูเก็ต ใช้เวลานานไหม?", a: "ปกติ 7–14 วัน — เราเตรียมเอกสารครบ ยื่นแทนได้ ลูกค้ามาเซ็นเอกสารและสัมภาษณ์เท่านั้น" },
      { q: "Honorary Consul ในภูเก็ตรับรองเอกสารแทนสถานทูตได้ทุกอย่างไหม?", a: "ทำได้สำหรับ Affidavit, POA, Single Status สำหรับ 22 ประเทศที่มีกงสุลในเกาะ — UK/USA/UAE ยังต้องส่งกรุงเทพ" },
    ],
  },
  {
    slug: "digital-nomads",
    emoji: "💻",
    titleTh: "Digital Nomad ในภูเก็ต",
    titleEn: "Digital Nomads in Phuket",
    shortTh: "Remote workers — DTV, LTR Work-from-Thailand, freelancer visa",
    metaTitle: "DTV Visa & Notary for Digital Nomads in Phuket",
    metaDesc: "DTV (180-day) and LTR Work-from-Thailand visas from Phuket. Proof of funds, employment letters, health insurance — translated and notarized on-site.",
    hero: {
      headline: "DTV / LTR สำหรับ Digital Nomad — ใช้ชีวิตและทำงาน Remote จากภูเก็ต",
      sub: "เตรียมเอกสาร Proof of Funds, Employment Letter, Health Insurance ครบจบในเกาะ — เริ่ม 18,000 บาท",
    },
    pains: [
      "DTV ต้องการ Proof of Funds 500,000 บาท + Employment Letter ที่รับรองถูกต้อง",
      "LTR Work-from-Thailand รายได้ $80k+/ปี ต้องเอกสารภาษีจากประเทศต้นทาง",
      "ต้องการคนแปลและรับรองเอกสารแบบรวดเร็วเพื่อยื่นที่ ตม. ภูเก็ต",
    ],
    services: ["notary-en", "translate-all", "thai-visa", "wp"],
    areas: ["patong", "rawai", "kata", "phuket-old-town", "cherngtalay"],
    visas: ["DTV (Destination Thailand Visa)", "LTR Work-from-Thailand", "Education (ED)", "SMART Visa"],
    faq: [
      { q: "DTV ทำที่ภูเก็ตได้เลยไหม หรือต้องกลับประเทศตัวเอง?", a: "ยื่นได้ที่สถานทูตไทยในประเทศตัวเองหรือประเทศที่สาม — เราเตรียมเอกสารทั้งชุดให้คุณนำไปยื่น" },
      { q: "Co-working space ในภูเก็ตที่แนะนำสำหรับ Digital Nomad?", a: "KoHub, Lets Work, Hatch, Garage Society Phuket, Worca — เราเดินทาง on-site ถึงทุกแห่ง" },
    ],
  },
  {
    slug: "yacht-crew",
    emoji: "⛵",
    titleTh: "ลูกเรือยอตช์ & กัปตัน",
    titleEn: "Yacht Crew & Captains",
    shortTh: "Seafarer documents, Yacht Charter, Crew Visa, Bareboat agreements",
    metaTitle: "Yacht Crew Notary & Seafarer Documents in Phuket",
    metaDesc: "Notarize Seaman's Book, Certificate of Competency, Yacht Charter & Bareboat Agreements at Royal Phuket Marina, Boat Lagoon and Yacht Haven on-site.",
    hero: {
      headline: "Notary & เอกสารทางทะเล — ที่มารีน่าทุกแห่งในภูเก็ต",
      sub: "Royal Phuket Marina • Boat Lagoon • Yacht Haven • Ao Po Grand Marina • Chalong Bay • Rassada Pier",
    },
    pains: [
      "ต้อง Notarize Seaman's Book / CoC เพื่อสมัครงานบนเรือต่างชาติ",
      "Yacht Charter Agreement ต้อง Notary และรับรองกงสุลก่อนใช้กับลูกค้า",
      "Crew Visa ต้องการเอกสารทางทะเลแปลและรับรองครบ",
    ],
    services: ["notary-en", "translate-all", "embassy", "police-intl"],
    areas: ["chalong", "ao-por", "rassada", "royal-phuket-marina", "boat-lagoon", "yacht-haven"],
    visas: ["Crew Visa (Non-B Marine)", "LTR (Wealthy Pensioner — Yacht Owners)", "Tourist + Extension"],
    faq: [
      { q: "Notary ที่มารีน่าทำได้จริงไหม?", a: "ได้ — ทนายเดินทางถึงเรือ/สำนักงานมารีน่าโดยตรง ค่าเดินทาง on-site 500–1,500 บาท ตามระยะทาง" },
      { q: "Bareboat Charter ต้องใช้เอกสารอะไรบ้าง?", a: "Yachtmaster/IYT License, Insurance, ID, Charter Agreement — เราทำคำรับรองทั้งชุดในวันเดียว" },
    ],
  },
  {
    slug: "property-buyers",
    emoji: "🏠",
    titleTh: "นักลงทุนวิลล่า/คอนโดในภูเก็ต",
    titleEn: "Villa & Condo Investors in Phuket",
    shortTh: "Due diligence, POA, Sale & Purchase, Leasehold 30 ปี, FET form",
    metaTitle: "Property POA & Notary for Phuket Real Estate",
    metaDesc: "Buying villas or condos in Phuket — Due Diligence, POA, Sale & Purchase, FET form and Land Office transfer handled end-to-end by our Phuket lawyers.",
    hero: {
      headline: "ซื้อวิลล่า/คอนโดในภูเก็ต — ครบทุกขั้นตอนถึงโอนกรรมสิทธิ์",
      sub: "Due Diligence โฉนด • POA • Sale & Purchase • FET • สำนักงานที่ดินจังหวัดภูเก็ต — เริ่ม 25,000 บาท",
    },
    pains: [
      "ไม่อยู่ในไทยช่วงโอนกรรมสิทธิ์ ต้อง POA",
      "Foreign Quota คอนโดต่ำกว่า 49% หรือ Leasehold 30+30+30 ปี",
      "ตรวจสอบโฉนด/นส.3 และผู้พัฒนาก่อนวางมัดจำ",
      "ต้องโอนเงินจากต่างประเทศและออก FET Form",
    ],
    services: ["notary-en", "translate-all", "embassy", "marriage-th"],
    areas: ["bang-tao", "cherngtalay", "kamala", "rawai", "kata", "patong", "nai-yang", "mai-khao"],
    visas: ["Investor LTR (Wealthy Global Citizen)", "Non-O (Property)", "Thailand Privilege"],
    faq: [
      { q: "ต่างชาติซื้อคอนโดในภูเก็ตได้ไหม?", a: "ได้ ภายใต้ Foreign Quota 49% ของพื้นที่ขายทั้งหมดในคอนโดนั้น และเงินต้องโอนจากต่างประเทศพร้อม FET" },
      { q: "Villa ซื้อในชื่อตัวเองได้ไหม?", a: "ที่ดินซื้อในชื่อต่างชาติไม่ได้ — ใช้ Leasehold 30 ปี (ต่อได้ 2 ครั้ง) หรือถือผ่าน Thai Company / คู่สมรสไทย" },
    ],
  },
  {
    slug: "marriage",
    emoji: "💍",
    titleTh: "คู่รักไทย–ต่างชาติในภูเก็ต",
    titleEn: "Thai–Foreign Couples in Phuket",
    shortTh: "Marriage registration ที่อำเภอเมือง/กะทู้/ถลาง — Affidavit, แปล, สถานทูต",
    metaTitle: "Marriage Registration Thai–Foreign in Phuket",
    metaDesc: "Marriage for Thai–foreign couples in Phuket — Affidavit of Single Status, embassy attestation, translation, district filing in Mueang/Kathu/Thalang.",
    hero: {
      headline: "จดทะเบียนสมรสไทย–ต่างชาติในภูเก็ต — ครบจบใน 10 วัน",
      sub: "อำเภอเมืองภูเก็ต • อำเภอกะทู้ (ป่าตอง) • อำเภอถลาง (เชิงทะเล/ลากูน่า) — เริ่ม 12,000 บาท",
    },
    pains: [
      "Affidavit of Single Status จากสถานทูตประเทศคู่ครอง",
      "แปลภาษาไทยและรับรอง MFA",
      "เลือกอำเภอที่ใกล้บ้าน + จัดคิวกับนายทะเบียน",
    ],
    services: ["marriage-th", "translate-all", "embassy", "notary-en"],
    areas: ["phuket-old-town", "patong", "cherngtalay", "rawai"],
    visas: ["Non-O (Marriage)", "LTR (Wealthy Global Citizen + Dependent)"],
    faq: [
      { q: "อยู่ป่าตอง จดที่อำเภอไหน?", a: "อำเภอกะทู้ (ครอบคลุมป่าตอง กะทู้ กมลา)" },
      { q: "อยู่เชิงทะเล/ลากูน่า/ในยาง?", a: "อำเภอถลาง — ใกล้ที่สุดและคนน้อยกว่าเมือง" },
    ],
  },
  {
    slug: "business-owners",
    emoji: "🏨",
    titleTh: "เจ้าของกิจการในภูเก็ต",
    titleEn: "Business Owners in Phuket",
    shortTh: "โรงแรม วิลล่าให้เช่า ทัวร์ ดำน้ำ ร้านอาหาร — Company, BOI, License",
    metaTitle: "Company Setup & BOI for Phuket Tourism Businesses",
    metaDesc: "Start a hotel, dive shop, tour company or restaurant in Phuket — Thai Ltd Company, BOI (Tourism/Wellness/Marine), TBL and work permits, handled locally.",
    hero: {
      headline: "ตั้งบริษัทท่องเที่ยว/โรงแรม/ดำน้ำในภูเก็ต — One-stop Legal",
      sub: "Thai Limited Company • BOI Tourism / Wellness / Marine • Hotel License • TBL • PADI Dive Center • Work Permit",
    },
    pains: [
      "Foreign Quota ในบริษัทไทย 49% — ต้องวางโครงสร้าง shareholder ให้ถูกต้อง",
      "ใบอนุญาตโรงแรม / TBL / Dive Center มีเงื่อนไขเฉพาะของภูเก็ต",
      "Work Permit ของ Manager/Instructor ต่างชาติต้องสอดคล้องกับใบประกอบกิจการ",
    ],
    services: ["notary-en", "translate-all", "wp", "embassy", "marriage-service"],
    areas: ["patong", "kata", "chalong", "cherngtalay", "phuket-old-town"],
    visas: ["BOI Visa", "Non-B (Business)", "LTR (Highly-Skilled Professional)", "SMART Visa"],
    faq: [
      { q: "BOI ในภูเก็ตได้สิทธิ์อะไรบ้าง?", a: "Tourism, Hospitality, Wellness, Marine, Software — ได้ Work Permit อัตโนมัติ ภาษีลดหย่อน 3–8 ปี ถือที่ดินเพื่อกิจการได้" },
      { q: "ตั้ง Dive Center ต้องมีอะไร?", a: "PADI/SSI affiliation + Thai Company + Tourism License + Marine Department permit + Insurance" },
    ],
  },
  {
    slug: "retirees",
    emoji: "🌅",
    titleTh: "ผู้เกษียณในภูเก็ต",
    titleEn: "Retirees in Phuket",
    shortTh: "Retirement Visa O-A/O-X, LTR Pensioner, Thai Will, Healthcare POA",
    metaTitle: "Retirement Visa & Estate Planning for Phuket Retirees",
    metaDesc: "Retirement Visa (O-A/O-X), LTR Pensioner, Thai Last Will and Healthcare POA for retirees in Phuket — on-site service at Rawai, Chalong, Kata and beyond.",
    hero: {
      headline: "เกษียณอายุที่ภูเก็ต — Visa, Will, Healthcare POA",
      sub: "O-A / O-X / LTR Pensioner • Thai Will & Estate Planning • Living Will & Healthcare POA",
    },
    pains: [
      "ต่อ Retirement Visa ทุกปี — เงินฝาก 800k หรือรายได้ 65k/เดือน",
      "ประกันสุขภาพตามเกณฑ์ ตม. (40k OPD / 400k IPD)",
      "ต้องการ Thai Will แยกจากพินัยกรรมประเทศตัวเอง สำหรับทรัพย์สินในไทย",
    ],
    services: ["thai-visa", "wp", "notary-en", "translate-all", "police-intl"],
    areas: ["rawai", "chalong", "kata", "kamala", "cherngtalay"],
    visas: ["Retirement O-A (1 ปี)", "Retirement O-X (5+5 ปี)", "LTR Wealthy Pensioner (10 ปี)"],
    faq: [
      { q: "O-A กับ LTR Pensioner ต่างกันยังไง?", a: "O-A ต่อปีละครั้ง 800k บาทค้างบัญชี — LTR Pensioner 10 ปี รายได้ $80k/ปี + ประกันสุขภาพ ได้ Work Permit ภาษี 17%" },
      { q: "Thai Will ทำที่ภูเก็ตได้ไหม?", a: "ได้ — ทนายเตรียมร่าง 2 ภาษา (ไทย-อังกฤษ) Notary และเก็บต้นฉบับให้ ค่าบริการเริ่ม 8,000 บาท" },
    ],
  },
  {
    slug: "students",
    emoji: "🎓",
    titleTh: "นักเรียนต่างชาติในภูเก็ต",
    titleEn: "International Students in Phuket",
    shortTh: "ED Visa, BIS/UWC/HeadStart, university enrollment, transcript translation",
    metaTitle: "Student Visa (ED) & School Document Translation in Phuket",
    metaDesc: "ED Visa, school enrollment and transcript & diploma translation for students at BIS, UWC, HeadStart, QSI, Kajonkiet and PIA in Phuket — handled end-to-end.",
    hero: {
      headline: "ED Visa & เอกสารการศึกษา — สำหรับครอบครัวต่างชาติในภูเก็ต",
      sub: "BIS • UWC Thailand • HeadStart • QSI • Kajonkiet International • PIA — แปล Transcript รับรอง MFA + สถานทูต",
    },
    pains: [
      "ED Visa ต้องการ Acceptance Letter + Police Clearance + Health Certificate",
      "แปล Transcript / Diploma เพื่อสมัครเรียนต่อระดับอุดมศึกษา",
      "Guardianship Affidavit สำหรับนักเรียนที่อายุต่ำกว่า 18 ปี",
    ],
    services: ["translate-all", "naati", "police-intl", "notary-en", "embassy"],
    areas: ["cherngtalay", "phuket-old-town", "kathu", "thalang"],
    visas: ["ED Visa (Non-ED)", "Dependent Visa (พ่อแม่นักเรียน)"],
    faq: [
      { q: "BIS / UWC รับเด็กต่างชาติอายุเท่าไหร่?", a: "อนุบาล–เกรด 12 — เราช่วยเตรียมเอกสาร Birth Certificate, Previous School Records, Immunization แปลและรับรองครบ" },
    ],
  },
];

export const TOTAL_SEGMENTS = PHUKET_SEGMENTS.length;